/*
 * ActionBar: a row of actions with a clear hierarchy, for toolbars (a record's next steps, a
 * selection's actions) and dialog footers. Not in Figma: built from Button and MenuButton.
 *
 * The rules every screen follows (so a new view gets them by using this, not by repeating them):
 *
 *  1. One primary action: the next step (weight "primary"; the first one if several). It comes
 *     first, as in the design's dialogs (primary, then Cancel). `quiet` shows no primary, for a
 *     region where something else is the primary just now (Save while a form has changes).
 *  2. Few visible choices: in a toolbar, the primary and at most `inline` everyday actions (2);
 *     the rest goes under "More", everyday ones first, then occasional ones (print, preview,
 *     reset, lock…), then destructive ones (red, after a rule). On a phone only the primary and
 *     "More" show. In a dialog every action shows, Cancel last.
 *  3. Destructive actions are red where they are listed and are confirmed by the caller before
 *     they act (see app/confirm.tsx), in a dialog that names the action.
 *  4. Keyboard: an action's `hotkey` ("q", "shift+g", as Odoo writes them) runs it with Alt
 *     (Alt+Q, Alt+Shift+G). The hint shows in the button's tooltip and in the menu. Shortcuts of
 *     a bar under an open dialog don't fire.
 *  5. Feedback: while one action runs (`busy`) every action is disabled, the running one marked
 *     aria-busy, so nothing is sent twice.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'
import { Button } from './Button'
import { cx } from './cx'
import { MenuButton } from './MenuButton'
import type { MenuItem } from './MenuButton'
import { PHONE, useMediaQuery } from './useMediaQuery'

export interface BarAction {
    key: string
    label: string
    onPress: () => void
    /** primary: the next step; normal: everyday; occasional: rarely needed or undoing. Default normal. */
    weight?: 'primary' | 'normal' | 'occasional'
    /** Destroys or cancels something: red, last. */
    danger?: boolean
    /** Odoo's data-hotkey ("q", "shift+g"): runs with Alt. */
    hotkey?: string
    disabled?: boolean
}

/** "shift+g" → "Alt+Shift+G". */
export function hotkeyLabel(hotkey: string): string {
    return ['Alt', ...hotkey.split('+').map((p) => (p.length === 1 ? p.toUpperCase() : p[0]!.toUpperCase() + p.slice(1)))].join('+')
}

/** The actions in their places: the primary, the inline ones, the menu's items in order. */
export function arrange(actions: BarAction[], opts: { inline: number; quiet?: boolean; dialog?: boolean }) {
    const primary = opts.quiet ? null : actions.find((a) => a.weight === 'primary') ?? null
    const rest = actions.filter((a) => a !== primary)
    if (opts.dialog) {
        // Everything visible; cancel-like actions last.
        return { primary, inline: [...rest.filter((a) => a.weight !== 'occasional'), ...rest.filter((a) => a.weight === 'occasional')], more: [] as BarAction[] }
    }
    const everyday = rest.filter((a) => a.weight !== 'occasional' && !a.danger)
    const inline = everyday.slice(0, opts.inline)
    const more = [
        ...everyday.slice(opts.inline),
        ...rest.filter((a) => a.weight === 'occasional' && !a.danger),
        ...rest.filter((a) => a.danger && !inline.includes(a)),
    ]
    return { primary, inline, more }
}

/** Alt+key shortcuts for a set of actions, ignored while a dialog above `root` is open. */
export function useActionHotkeys(actions: BarAction[], root: RefObject<HTMLElement | null>, enabled = true) {
    const latest = useRef(actions)
    useLayoutEffect(() => {
        latest.current = actions
    })
    useEffect(() => {
        if (!enabled) return
        const onKey = (e: KeyboardEvent) => {
            if (!e.altKey || e.ctrlKey || e.metaKey) return
            const key = e.code.startsWith('Key') ? e.code.slice(3).toLowerCase() : e.code.startsWith('Digit') ? e.code.slice(5) : null
            if (!key) return
            const dialogs = document.querySelectorAll('[role="dialog"][aria-modal="true"]')
            const top = dialogs[dialogs.length - 1]
            if (top && root.current && !top.contains(root.current)) return
            const wanted = `${e.shiftKey ? 'shift+' : ''}${key}`
            const action = latest.current.find((a) => a.hotkey === wanted && !a.disabled)
            if (!action) return
            e.preventDefault()
            ;(document.activeElement as HTMLElement | null)?.blur()
            setTimeout(action.onPress, 0)
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [root, enabled])
}

export function ActionBar({ actions, moreLabel, extra = [], inline = 2, quiet, dialog, busy, className }: {
    actions: BarAction[]
    moreLabel: string
    /** More items for the "More" menu, after the actions that didn't fit (e.g. a list's bulk actions). */
    extra?: MenuItem[]
    /** Everyday actions shown beside the primary in a toolbar (default 2). */
    inline?: number
    quiet?: boolean
    /** A dialog's footer: every action shows. */
    dialog?: boolean
    /** The key of the action running now (all are disabled meanwhile). */
    busy?: string | boolean | null
    className?: string
}) {
    const ref = useRef<HTMLDivElement>(null)
    const phone = useMediaQuery(PHONE)
    // The action pressed last: it shows the spinner while the bar is busy.
    const [pressed, setPressed] = useState<string | null>(null)
    const tracked = actions.map((a) => ({ ...a, onPress: () => { setPressed(a.key); a.onPress() } }))
    useActionHotkeys(tracked, ref, !busy)
    const { primary, inline: shown, more } = arrange(tracked, { inline: phone && !dialog ? 0 : inline, quiet, dialog })
    const tip = (a: BarAction) => (a.hotkey ? `${a.label} (${hotkeyLabel(a.hotkey)})` : undefined)
    const button = (a: BarAction, variant: 'primary' | 'secondary') => (
        <Button key={a.key} variant={variant} disabled={Boolean(busy) || a.disabled} busy={Boolean(busy) && (busy === a.key || pressed === a.key)} title={tip(a)}
            className={cx(a.danger && variant === 'secondary' && 'text-[var(--text-danger)]')} onClick={a.onPress}>
            {a.label}
        </Button>
    )
    const items: MenuItem[] = more.map((a, i) => ({
        key: a.key, label: a.label, onPick: a.onPress, danger: a.danger, disabled: a.disabled,
        hint: a.hotkey ? hotkeyLabel(a.hotkey) : undefined,
        divider: i > 0 && ((a.danger && !more[i - 1]!.danger) || (a.weight === 'occasional' && more[i - 1]!.weight !== 'occasional' && !more[i - 1]!.danger)),
    }))
    if (extra.length) items.push(...extra.map((x, i) => (i === 0 && items.length ? { ...x, divider: true } : x)))
    if (!primary && !shown.length && !items.length) return null
    return (
        <div ref={ref} className={cx('flex flex-wrap items-center gap-[8px]', className)}>
            {primary && button(primary, 'primary')}
            {shown.map((a) => button(a, 'secondary'))}
            <MenuButton variant="ghost" label={moreLabel} items={items} disabled={Boolean(busy)} />
        </div>
    )
}
